import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Minus, Plus, ImageOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { inr, shiftDate } from "@/lib/format";
import { cn } from "@/lib/utils";

export const PageHeader = ({ eyebrow, title, subtitle, children }) => (
  <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-8 bf-rise">
    <div>
      {eyebrow && <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D96B43] mb-2">{eyebrow}</div>}
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0F382C]" data-testid="page-title">{title}</h1>
      {subtitle && <p className="text-sm md:text-base text-muted-foreground mt-2 max-w-2xl">{subtitle}</p>}
    </div>
    {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
  </div>
);

const TONES = {
  default: "bg-white border-[#E7E2D6]",
  amber: "bg-amber-50/70 border-amber-200 border-l-4 border-l-amber-500",
  green: "bg-emerald-50/70 border-emerald-200",
  rose: "bg-rose-50/70 border-rose-200",
  dark: "bg-[#0F382C] border-[#0F382C] text-white",
};

export const StatCard = ({ label, value, hint, icon: Icon, tone = "default", testid }) => (
  <div className={cn("rounded-2xl border p-5 shadow-sm transition-shadow hover:shadow-md bf-rise", TONES[tone])} data-testid={testid}>
    <div className="flex items-start justify-between">
      <div className={cn("text-xs font-semibold uppercase tracking-wider", tone === "dark" ? "text-emerald-200" : "text-muted-foreground")}>{label}</div>
      {Icon && <Icon className={cn("h-5 w-5", tone === "dark" ? "text-emerald-300" : "text-[#0F382C]/60")} />}
    </div>
    <div className="font-mono text-2xl sm:text-3xl font-semibold mt-3 tracking-tight" data-testid={testid ? `${testid}-value` : undefined}>{value}</div>
    {hint && <div className={cn("text-xs mt-2", tone === "dark" ? "text-emerald-100/80" : "text-muted-foreground")}>{hint}</div>}
  </div>
);

const PILL = {
  confirmed: "bg-emerald-100 text-emerald-800 border-emerald-300",
  delivered: "bg-emerald-100 text-emerald-800 border-emerald-300",
  received: "bg-emerald-100 text-emerald-800 border-emerald-300",
  paid: "bg-emerald-100 text-emerald-800 border-emerald-300",
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  credit: "bg-amber-100 text-amber-800 border-amber-300",
  skipped: "bg-slate-100 text-slate-700 border-slate-300",
  paused: "bg-sky-100 text-sky-800 border-sky-300",
  failed: "bg-rose-100 text-rose-700 border-rose-300",
  issue: "bg-rose-100 text-rose-700 border-rose-300",
  low: "bg-rose-100 text-rose-700 border-rose-300",
};

const WA_LABEL = { accepted: "Queued", sent: "Sent", delivered: "Delivered", read: "Read", replied: "Replied", failed: "Failed" };
const WA_TONE = {
  accepted: "bg-slate-100 text-slate-700", sent: "bg-sky-100 text-sky-800", delivered: "bg-emerald-100 text-emerald-800",
  read: "bg-emerald-600 text-white", replied: "bg-[#0F382C] text-white", failed: "bg-rose-100 text-rose-700",
};

export const WaStatus = ({ msg, testid }) => {
  if (!msg) return null;
  const s = msg.status || (msg.ok ? "accepted" : "failed");
  return (
    <span title={msg.error || ""} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${WA_TONE[s] || WA_TONE.accepted}`} data-testid={testid}>
      WhatsApp · {WA_LABEL[s] || s}
    </span>
  );
};

export const StatusPill = ({ status, label, testid }) => (
  <span className={cn("inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold capitalize whitespace-nowrap", PILL[status] || PILL.skipped)} data-testid={testid}>
    {label || status}
  </span>
);

export const Money = ({ value, className, testid }) => (
  <span className={cn("font-mono tabular-nums", className)} data-testid={testid}>{inr(value)}</span>
);

export const DateNav = ({ value, onChange, testid = "date-nav" }) => (
  <div className="flex items-center gap-1 rounded-xl border border-[#E7E2D6] bg-white p-1 shadow-sm" data-testid={testid}>
    <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => onChange(shiftDate(value, -1))} data-testid={`${testid}-prev`}>
      <ChevronLeft className="h-4 w-4" />
    </Button>
    <input type="date" value={value} onChange={(e) => e.target.value && onChange(e.target.value)}
      className="bg-transparent px-2 text-sm font-semibold outline-none" data-testid={`${testid}-input`} />
    <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => onChange(shiftDate(value, 1))} data-testid={`${testid}-next`}>
      <ChevronRight className="h-4 w-4" />
    </Button>
  </div>
);

export const NativeSelect = ({ className, children, ...props }) => (
  <select className={cn("h-10 w-full rounded-md border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[#0F382C]/30", className)} {...props}>
    {children}
  </select>
);

export const Empty = ({ icon: Icon, title, text, testid }) => (
  <div className="rounded-2xl border border-dashed border-[#D9D2C0] bg-white/60 p-10 text-center" data-testid={testid}>
    {Icon && <Icon className="mx-auto h-8 w-8 text-[#0F382C]/40" />}
    <div className="font-display font-semibold mt-3 text-[#0F382C]">{title}</div>
    {text && <p className="text-sm text-muted-foreground mt-1">{text}</p>}
  </div>
);

export const ItemsEditor = ({ products, value, onChange, prefix = "items" }) => {
  const qty = (id) => value.find((v) => v.product_id === id)?.qty || 0;
  const set = (id, q) => {
    const rest = value.filter((v) => v.product_id !== id);
    onChange(q > 0 ? [...rest, { product_id: id, qty: q }] : rest);
  };
  return (
    <div className="space-y-2">
      {products.filter((p) => p.active !== false).map((p) => (
        <div key={p.id} className="flex items-center justify-between gap-3 rounded-xl border border-[#E7E2D6] bg-white p-3" data-testid={`${prefix}-row-${p.id}`}>
          <div className="min-w-0">
            <div className="font-semibold text-[#0F172A] truncate">{p.name}</div>
            <div className="text-xs text-muted-foreground">{p.unit_label} · {inr(p.price)}</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button type="button" variant="outline" size="icon" className="h-10 w-10 rounded-full" onClick={() => set(p.id, Math.max(qty(p.id) - 1, 0))} data-testid={`${prefix}-minus-${p.id}`}>
              <Minus className="h-4 w-4" />
            </Button>
            <span className="w-8 text-center font-mono text-lg font-semibold" data-testid={`${prefix}-qty-${p.id}`}>{qty(p.id)}</span>
            <Button type="button" size="icon" className="h-10 w-10 rounded-full bg-[#0F382C] hover:bg-[#134E3A]" onClick={() => set(p.id, qty(p.id) + 1)} data-testid={`${prefix}-plus-${p.id}`}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

export const AuthImage = ({ path, className, alt = "Delivery proof", testid }) => {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let obj;
    api.get(path, { responseType: "blob" })
      .then((r) => { obj = URL.createObjectURL(r.data); setUrl(obj); })
      .catch(() => setErr(true));
    return () => obj && URL.revokeObjectURL(obj);
  }, [path]);
  if (err) return <div className={cn("grid place-items-center bg-slate-100 text-slate-400", className)}><ImageOff className="h-5 w-5" /></div>;
  if (!url) return <div className={cn("animate-pulse bg-slate-200", className)} />;
  return <img src={url} alt={alt} className={cn("object-cover", className)} data-testid={testid} />;
};
